/**
 * Metriek & Meetkunde (MET) Support Registry
 *
 * Support content (hints, explanations, visuals) for MET families.
 *
 * Module: MET (Metriek & Meetkunde)
 * Families: 39 (taxonomy) + 1 orphan (MET-TIJD-DUUR)
 * Coverage: 100% taxonomy + 1 orphan
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 *
 * Note: MET-TIJD-DUUR is an orphan (not in taxonomy) but kept for backwards compatibility
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * MET Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const MET_SUPPORT: Record<string, FamilySupportEntry> = {
  'MET-LEN-OMREKEN': {
    familyId: 'MET-LEN-OMREKEN',
    defaultExplanation: 'Gebruik het metriek trapje: km → hm → dam → m → dm → cm → mm. Elke stap naar beneden = ×10, elke stap naar boven = ÷10.',
    defaultVisual: {
      type: 'stairs',
      data: {
        type: 'length',
        showAnimation: true
      },
      showWhen: 'always'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: 'Gebruik het metriek trapje: km → hm → dam → m → dm → cm → mm',
      visual: {
        type: 'stairs',
        data: {
          type: 'length',
          showAnimation: true
        }
      }
    }, {
      title: 'Naar beneden = ×10',
      content: 'Elke stap naar beneden vermenigvuldig je met 10 (een nul erbij).\nVoorbeeld: 3 m = 30 dm = 300 cm = 3.000 mm'
    }, {
      title: 'Naar boven = ÷10',
      content: 'Elke stap naar boven deel je door 10 (een nul eraf).\nVoorbeeld: 5.000 mm = 500 cm = 50 dm = 5 m'
    }]
  },
  'MET-LEN-SCHATTEN': {
    familyId: 'MET-LEN-SCHATTEN',
    defaultExplanation: 'Lengtes schatten: gebruik referentiematen. Een deur is ±2 m, een pen ±15 cm, een duim ±2 cm.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Referentiematen',
      content: 'Duim = ±2 cm\nPen = ±15 cm\nLiniaal = 30 cm\nDeur = ±2 m\nKlasLokaal = ±8 m\nVoetbalveld = ±100 m'
    }]
  },
  'MET-LEN-METEN': {
    familyId: 'MET-LEN-METEN',
    defaultExplanation: 'Meten met een liniaal: leg de 0 precies bij het begin. Lees af bij het einde van het voorwerp.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Leg de 0 bij het begin\nStap 2: Kijk waar het voorwerp eindigt\nStap 3: Lees de maat af\nStap 4: Let op de eenheid (cm of mm)'
    }]
  },
  'MET-LEN-NAUWKEURIG': {
    familyId: 'MET-LEN-NAUWKEURIG',
    defaultExplanation: 'Nauwkeurig meten: lees af in millimeters. Tussen twee streepjes op de liniaal zit 1 mm.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Millimeters',
      content: 'Grote streepjes = centimeters\nKleine streepjes = millimeters\n10 mm = 1 cm\n\nLees altijd af op ooghoogte!'
    }]
  },
  'MET-LEN-CONTEXT': {
    familyId: 'MET-LEN-CONTEXT',
    defaultExplanation: 'Lengtes in context: kies de juiste eenheid. Afstand tussen steden in km, hoogte van een persoon in cm.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Juiste eenheid kiezen',
      content: 'mm: dikte van papier, munt\ncm: lengte van een pen, vinger\nm: hoogte van een persoon, kamer\nkm: afstand tussen steden'
    }]
  },
  'MET-GEW-OMREKEN': {
    familyId: 'MET-GEW-OMREKEN',
    defaultExplanation: 'Gewicht omrekenen: 1 kg = 1.000 g, 1 ton = 1.000 kg. Gebruik het metriek trapje.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Omrekenen',
      content: '1 ton = 1.000 kg\n1 kg = 1.000 g\n1 g = 1.000 mg\n\nNaar beneden = ×1.000\nNaar boven = ÷1.000'
    }]
  },
  'MET-GEW-SCHATTEN': {
    familyId: 'MET-GEW-SCHATTEN',
    defaultExplanation: 'Gewicht schatten: gebruik referenties. Een appel ≈ 150 g, een pak melk ≈ 1 kg, een auto ≈ 1.500 kg.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Referenties',
      content: 'Paperclip ≈ 1 g\nAppel ≈ 150 g\nPak melk ≈ 1 kg\nFiets ≈ 15 kg\nAuto ≈ 1.500 kg'
    }]
  },
  'MET-GEW-WEGEN': {
    familyId: 'MET-GEW-WEGEN',
    defaultExplanation: 'Wegen met een weegschaal: lees de schaal af. Let op de eenheid en de verdeling van de streepjes.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Aflezen',
      content: 'Stap 1: Kijk naar de eenheid (g of kg)\nStap 2: Bepaal de waarde per streepje\nStap 3: Lees de wijzer af'
    }]
  },
  'MET-GEW-CONTEXT': {
    familyId: 'MET-GEW-CONTEXT',
    defaultExplanation: 'Gewicht in context: kies de juiste eenheid en reken om als nodig.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Juiste eenheid',
      content: 'mg: medicijnen\ng: voedsel, ingrediënten\nkg: personen, koffers\nton: auto, vrachtwagen'
    }]
  },
  'MET-INH-OMREKEN': {
    familyId: 'MET-INH-OMREKEN',
    defaultExplanation: 'Inhoud omrekenen: 1 l = 10 dl = 100 cl = 1.000 ml. Gebruik het metriek trapje.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Trapje',
      content: 'l → dl → cl → ml\nElke stap = ×10\n\n1 l = 10 dl = 100 cl = 1.000 ml'
    }]
  },
  'MET-INH-LITER': {
    familyId: 'MET-INH-LITER',
    defaultExplanation: 'De sleutelregel: 1 dm³ = 1 liter. Dus inhoud in dm³ is gelijk aan inhoud in liters. 1 m³ = 1.000 liter. 1 cm³ = 1 ml.',
    defaultVisual: {
      type: 'stairs',
      data: {
        type: 'volume',
        showAnimation: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: 'Het metriek stelsel voor inhoud werkt hetzelfde als voor lengte.',
      visual: {
        type: 'stairs',
        data: {
          type: 'volume',
          showAnimation: true
        }
      }
    }]
  },
  'MET-INH-OMREKEN-KUBIEK': {
    familyId: 'MET-INH-OMREKEN-KUBIEK',
    defaultExplanation: 'Kubieke maten omrekenen: 1 m³ = 1.000 dm³ = 1.000.000 cm³. Elke stap is ×1.000.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Kubiek omrekenen',
      content: '1 m³ = 1.000 dm³\n1 dm³ = 1.000 cm³ = 1 liter\n1 cm³ = 1 ml\n\nBij inhoud: elke stap = ×1.000 (niet ×10!)'
    }]
  },
  'MET-INH-CITER': {
    familyId: 'MET-INH-CITER',
    defaultExplanation: 'Kleine inhoudsmaten: 1 liter = 10 dl = 100 cl = 1.000 ml. cc (kubieke centimeter) = ml. Elke stap is ×10.',
    defaultVisual: {
      type: 'stairs',
      data: {
        type: 'volume',
        showAnimation: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: 'Het metriek stelsel voor inhoud werkt hetzelfde als voor lengte.',
      visual: {
        type: 'stairs',
        data: {
          type: 'volume',
          showAnimation: true
        }
      }
    }]
  },
  'MET-INH-BLOK': {
    familyId: 'MET-INH-BLOK',
    defaultExplanation: 'Inhoud van een balk = lengte × breedte × hoogte. Zorg dat alle maten in dezelfde eenheid staan voordat je rekent.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'box3d',
      data: {
        length: 5,
        width: 3,
        height: 4,
        unit: 'cm',
        showGrid: true
      },
      showWhen: 'wrong'
    },
    hints: [{
      content: 'Inhoud = lengte × breedte × hoogte\nLet op: zet eerst alle maten om naar dezelfde eenheid!',
      visual: {
        type: 'box3d',
        data: {
          length: 5,
          width: 3,
          height: 4,
          unit: 'cm',
          showGrid: true
        }
      }
    }, {
      title: 'Inhoud berekenen',
      content: 'Inhoud = lengte × breedte × hoogte\nVoorbeeld: 5 cm × 3 cm × 4 cm = 60 cm³'
    }]
  },
  'MET-INH-SCHATTEN': {
    familyId: 'MET-INH-SCHATTEN',
    defaultExplanation: 'Inhoud schatten: een glas ≈ 200 ml, een fles ≈ 1 l, een emmer ≈ 10 l, een bad ≈ 150 l.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Referenties',
      content: 'Theelepel ≈ 5 ml\nGlas ≈ 200 ml\nFles ≈ 1 l\nEmmer ≈ 10 l\nBad ≈ 150 l'
    }]
  },
  'MET-OPP-RECHT': {
    familyId: 'MET-OPP-RECHT',
    defaultExplanation: 'Oppervlakte van een rechthoek = lengte × breedte. Bij een vierkant: zijde × zijde. Eenheid is altijd in het kwadraat (cm², m²).',
    defaultVisual: {
      type: 'geo',
      data: {
        width: 6,
        height: 4,
        unit: 'cm',
        showGrid: true,
        showFormula: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Formule',
      content: 'Oppervlakte = lengte × breedte\nBij een vierkant: zijde × zijde'
    }, {
      title: 'Terugrekenen',
      content: 'Als je de oppervlakte weet en één zijde:\nlengte = oppervlakte ÷ breedte'
    }]
  },
  'MET-OMTREK-RECHT': {
    familyId: 'MET-OMTREK-RECHT',
    defaultExplanation: 'Omtrek van een rechthoek = 2 × lengte + 2 × breedte. Of: 2 × (lengte + breedte).',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Formule',
      content: 'Omtrek = 2 × (l + b)\n\nVoorbeeld: l=6, b=4\nOmtrek = 2 × (6+4) = 2 × 10 = 20 cm'
    }]
  },
  'MET-OPP-DRIEHOEK': {
    familyId: 'MET-OPP-DRIEHOEK',
    defaultExplanation: 'Oppervlakte driehoek = basis × hoogte ÷ 2. De hoogte staat loodrecht op de basis.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Formule',
      content: 'Opp = basis × hoogte ÷ 2\n\nVoorbeeld: basis=8, hoogte=5\nOpp = 8 × 5 ÷ 2 = 20 cm²\n\nLet op: de hoogte staat LOODRECHT op de basis!'
    }]
  },
  'MET-OPP-SAMENGESTELD': {
    familyId: 'MET-OPP-SAMENGESTELD',
    defaultExplanation: 'Samengestelde figuren: verdeel de figuur in bekende vormen (rechthoeken, driehoeken), bereken elk apart en tel op.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Strategie',
      content: 'Stap 1: Verdeel in bekende vormen\nStap 2: Bereken elke oppervlakte apart\nStap 3: Tel alle oppervlaktes op\n\nOf: bereken het grote geheel en trek het ontbrekende stuk eraf.'
    }]
  },
  'MET-OPP-LAND': {
    familyId: 'MET-OPP-LAND',
    defaultExplanation: 'Landmaten: 1 are = 100 m², 1 hectare = 10.000 m² = 100 are, 1 km² = 100 hectare. Bij oppervlakte gaat elke stap met ×100.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Belangrijk verschil!',
      content: 'Bij oppervlakte gaat elke stap met ×100 (niet ×10!).\n1 dm² = 100 cm²\n1 m² = 100 dm² = 10.000 cm²\n1 km² = 1.000.000 m²'
    }]
  },
  'MET-OPP-VIERKANT': {
    familyId: 'MET-OPP-VIERKANT',
    defaultExplanation: 'Oppervlakte vierkant = zijde × zijde = zijde². Alle zijden zijn even lang.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Formule',
      content: 'Opp = zijde × zijde = zijde²\n\nVoorbeeld: zijde = 5 cm\nOpp = 5 × 5 = 25 cm²'
    }]
  },
  'MET-OPP-PARALLELLOGRAM': {
    familyId: 'MET-OPP-PARALLELLOGRAM',
    defaultExplanation: 'Oppervlakte parallellogram = basis × hoogte. De hoogte staat loodrecht op de basis (niet de schuine zijde!).',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Formule',
      content: 'Opp = basis × hoogte\n\nLet op: gebruik de LOODRECHTE hoogte,\nniet de schuine zijde!'
    }]
  },
  'MET-OPP-OMREKENEN': {
    familyId: 'MET-OPP-OMREKENEN',
    defaultExplanation: 'Oppervlakte-eenheden omrekenen: elke stap is ×100 (niet ×10!). 1 m² = 100 dm² = 10.000 cm².',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Belangrijk!',
      content: 'Bij oppervlakte: elke stap = ×100\n1 km² = 100 ha = 1.000.000 m²\n1 m² = 100 dm² = 10.000 cm²\n1 dm² = 100 cm²\n\nNiet ×10 zoals bij lengte!'
    }]
  },
  'MET-MEETK-OMTREK': {
    familyId: 'MET-MEETK-OMTREK',
    defaultExplanation: 'Omtrek van een cirkel = π × diameter = 2 × π × straal. Gebruik π ≈ 3,14. Voorbeeld: straal 5 cm → omtrek = 2 × 3,14 × 5 = 31,4 cm.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'circle',
      data: {
        radius: 5,
        showFormula: true,
        highlightRadius: true,
        label: 'Omtrek = 2πr'
      },
      showWhen: 'wrong'
    },
    hints: [{
      title: 'Formule',
      content: 'Omtrek cirkel = π × diameter = 2 × π × straal\nGebruik π ≈ 3,14\n\nVoorbeeld: straal = 5 cm\nOmtrek = 2 × 3,14 × 5 = 31,4 cm'
    }, {
      title: 'Diameter vs straal',
      content: 'Straal = van middelpunt naar rand\nDiameter = van rand naar rand (= 2 × straal)\n\nKen je de diameter? Deel door 2 voor de straal.',
      visual: {
        type: 'circle',
        data: {
          radius: 5,
          highlightRadius: true,
          highlightDiameter: true,
          label: 'straal en diameter'
        }
      }
    }]
  },
  'MET-MEETK-OPP': {
    familyId: 'MET-MEETK-OPP',
    defaultExplanation: 'Oppervlakte van een cirkel = π × straal². Voorbeeld: straal 5 cm → opp = 3,14 × 25 = 78,5 cm². Let op: straal in het kwadraat!',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'circle',
      data: {
        radius: 5,
        showFormula: true,
        highlightRadius: true,
        label: 'Opp = πr²'
      },
      showWhen: 'wrong'
    },
    hints: [{
      title: 'Formule',
      content: 'Oppervlakte cirkel = π × straal²\nGebruik π ≈ 3,14\n\nVoorbeeld: straal = 5 cm\nOpp = 3,14 × 5² = 3,14 × 25 = 78,5 cm²'
    }]
  },
  'MET-MEETK-DIAMETER': {
    familyId: 'MET-MEETK-DIAMETER',
    defaultExplanation: 'Diameter = 2 × straal. Straal = diameter ÷ 2. De diameter gaat door het middelpunt van de cirkel.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Begrippen',
      content: 'Straal (r): middelpunt → rand\nDiameter (d): rand → rand door middelpunt\n\nd = 2r\nr = d ÷ 2'
    }]
  },
  'MET-MEETK-CIRKEL-DEEL': {
    familyId: 'MET-MEETK-CIRKEL-DEEL',
    defaultExplanation: 'Een cirkelsector is een "taartpunt" van de cirkel. De oppervlakte hangt af van de hoek: sector = (hoek/360) × π × r².',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Formule',
      content: 'Sector-oppervlakte = (hoek/360) × π × r²\n\nVoorbeeld: hoek=90°, r=4\n= (90/360) × 3,14 × 16\n= 0,25 × 50,24 = 12,56 cm²'
    }]
  },
  'MET-HOEK-METEN': {
    familyId: 'MET-HOEK-METEN',
    defaultExplanation: 'Hoeken meten met een geodriehoek: leg het middelpunt op het hoekpunt, de nullijn op één been, lees de hoek af op het andere been.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Leg middelpunt op hoekpunt\nStap 2: Leg nullijn op één been\nStap 3: Lees af waar het andere been de gradenboog kruist\nStap 4: Kies de juiste schaal (binnen of buiten)'
    }]
  },
  'MET-HOEK-TEKENEN': {
    familyId: 'MET-HOEK-TEKENEN',
    defaultExplanation: 'Hoeken tekenen: teken één been, leg de geodriehoek erop, markeer de gewenste hoek, trek het tweede been.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Teken een been (horizontale lijn)\nStap 2: Leg geodriehoek met middelpunt op beginpunt\nStap 3: Markeer de gewenste graden\nStap 4: Trek een lijn naar het markering'
    }]
  },
  'MET-HOEK-SOORTEN': {
    familyId: 'MET-HOEK-SOORTEN',
    bubbleHint: 'Vergelijk de hoek met een rechte hoek van 90°: kleiner = scherp, groter = stomp.',
    defaultExplanation: 'Hoeksoorten: scherpe hoek (< 90°), rechte hoek (= 90°), stompe hoek (> 90° en < 180°), gestrekte hoek (= 180°).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Soorten',
      content: 'Scherp: < 90° (spits)\nRecht: = 90° (vierkantje)\nStomp: > 90° en < 180° (wijd)\nGestrekt: = 180° (rechte lijn)'
    }]
  },
  'MET-HOEK-BEREKENEN': {
    familyId: 'MET-HOEK-BEREKENEN',
    defaultExplanation: 'Hoeken berekenen: de som van alle hoeken in een driehoek = 180°. In een vierhoek = 360°.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regels',
      content: 'Driehoek: alle hoeken samen = 180°\nVierhoek: alle hoeken samen = 360°\n\nOntbrekende hoek = totaal - bekende hoeken\nVoorbeeld: 60° + 80° + ? = 180° → ? = 40°'
    }]
  },
  'MET-DRIEHOEK-SOORTEN': {
    familyId: 'MET-DRIEHOEK-SOORTEN',
    bubbleHint: 'Tel eerst gelijke zijden, kijk dan naar de hoeken. Twee kenmerken samen geven het type.',
    defaultExplanation: 'Driehoeksoorten: gelijkzijdig (3 gelijke zijden), gelijkbenig (2 gelijke zijden), rechthoekig (1 rechte hoek).',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Soorten',
      content: 'Gelijkzijdig: 3 gelijke zijden, 3 × 60°\nGelijkbenig: 2 gelijke zijden, 2 gelijke hoeken\nRechthoekig: 1 hoek van 90°\nOngelijkzijdig: alle zijden verschillend'
    }]
  },
  'MET-SYMMETRIE': {
    familyId: 'MET-SYMMETRIE',
    defaultExplanation: 'Symmetrie: een figuur is symmetrisch als je het kunt vouwen over een lijn en beide helften precies op elkaar vallen.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Herkennen',
      content: 'Symmetrielijn = spiegellijn = vouwlijn\n\nVoorbeelden:\nVierkant: 4 symmetrielijnen\nRechthoek: 2 symmetrielijnen\nGelijkzijdige driehoek: 3 symmetrielijnen\nCirkel: oneindig veel!'
    }]
  },
  'MET-COORDINATEN': {
    familyId: 'MET-COORDINATEN',
    defaultExplanation: 'Coördinaten: (x, y) waarbij x horizontaal is en y verticaal. Eerst naar rechts, dan omhoog.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Aflezen',
      content: 'Punt (3, 5):\nx = 3 → 3 naar rechts\ny = 5 → 5 omhoog\n\nEzelsbruggetje: "Eerst de gang in (x), dan de trap op (y)"'
    }]
  },
  'MET-SCHAAL-BASIS': {
    familyId: 'MET-SCHAAL-BASIS',
    defaultExplanation: 'Schaal 1:100 betekent: 1 cm op de tekening = 100 cm (= 1 m) in het echt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Omrekenen',
      content: 'Schaal 1:100\nTekening → echt: ×100\nEcht → tekening: ÷100\n\n5 cm op tekening = 500 cm = 5 m echt\n3 m echt = 300 cm ÷ 100 = 3 cm op tekening'
    }]
  },
  'MET-SCHAAL-LENGTE': {
    familyId: 'MET-SCHAAL-LENGTE',
    defaultExplanation: 'Schaal bij lengte: meet de afstand op de kaart/tekening en vermenigvuldig met de schaalfactor.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Meet de afstand op de tekening (in cm)\nStap 2: Vermenigvuldig met de schaalfactor\nStap 3: Reken om naar de juiste eenheid\n\nSchaal 1:50.000, afstand 4 cm\n4 × 50.000 = 200.000 cm = 2 km'
    }]
  },
  'MET-SCHAAL-OPP': {
    familyId: 'MET-SCHAAL-OPP',
    defaultExplanation: 'Bij schaal en oppervlakte: de schaalfactor geldt in het kwadraat. Schaal 1:100 voor lengte → schaal 1:10.000 voor oppervlakte.',
    requiresHints: true,
    requiresVisual: true,
    defaultVisual: {
      type: 'ratio',
      data: {
        headers: ['Kaart', 'Echt'],
        rows: [[1, 100], ['1 cm²', '10.000 cm²']],
        highlight: [1, 1]
      },
      showWhen: 'wrong'
    },
    hints: [{
      title: 'Belangrijk verschil!',
      content: 'Bij oppervlakte gaat elke stap met ×100 (niet ×10!).\n1 dm² = 100 cm²\n1 m² = 100 dm² = 10.000 cm²\n1 km² = 1.000.000 m²'
    }]
  },
  'MET-TIJD-METEN': {
    familyId: 'MET-TIJD-METEN',
    defaultExplanation: 'Tijd meten met een stopwatch: druk op start, stop bij het einde. Lees seconden en tienden af.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Aflezen',
      content: 'Stopwatch toont: minuten:seconden.tienden\n01:23.4 = 1 minuut, 23 seconden en 4 tienden\n\n60 seconden = 1 minuut\n10 tienden = 1 seconde'
    }]
  },
  'MET-TIJD-NAUWKEURIG': {
    familyId: 'MET-TIJD-NAUWKEURIG',
    defaultExplanation: 'Nauwkeurig tijdmeten: lees af in seconden en tienden van seconden. Let op reactietijd bij handmatig stoppen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Nauwkeurigheid',
      content: 'Digitale stopwatch: tot op 0,01 seconde\nHandmatig stoppen: ±0,2 seconde reactietijd\n\nTip: meet meerdere keren en neem het gemiddelde.'
    }]
  },
  // ══════════════════════════════════════════════════════════
  // ORPHAN: MET-TIJD-DUUR (not in taxonomy, kept for backwards compatibility)
  // ══════════════════════════════════════════════════════════
  'MET-TIJD-DUUR': {
    familyId: 'MET-TIJD-DUUR',
    defaultExplanation: 'Tijdsduur berekenen: tel eerst naar het volgende hele uur, dan de hele uren, dan de resterende minuten. Let op: 1 uur = 60 minuten (niet 100!).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Tel eerst naar het volgende hele uur\nStap 2: Tel dan de hele uren\nStap 3: Tel de resterende minuten'
    }]
  }
};

// S-5 (Support RC audit 2026): removed unused `MET_SUPPORT_STATS` export.
// NOTE: MET has 1 orphan family `MET-TIJD-DUUR` (not in taxonomy, kept for
// backwards compatibility). This is documented in familySupportRegistry.ts.